import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { isString } from 'lodash-es';
import { StyledSiteStatusIndicator } from './SiteStatusIndicator.styles';
import { SiteStatusIndicatorTooltip } from './SiteStatusIndicatorTooltip';
import { SiteStatusIndicatorGlobalStyles } from './SiteStatusIndicator.global.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { Optional } from 'lib/types/Optional';
import { StatusPill, StatusPillProps } from 'lib/components/StatusPill/StatusPill';
import { getMachineStatus } from 'app/modules/machine-inventory/utils/getMachineStatus';
import {
  MachineStatus,
  IMachineStatusItemData,
  MachineStatusItemData,
} from 'app/modules/machine-inventory/interfaces/Machine.types';
import { Tooltip } from 'lib/components/Tooltip/Tooltip';
import { DataStatus } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';

export interface MachineStatusIndicatorProps {
  className?: string;
  lastActivityAt?: string | null;
  status?: MachineStatus;
  showText?: boolean;
  size?: 'small' | 'medium';
}

export const SiteStatusIndicator = ({
  className,
  lastActivityAt,
  status,
  showText,
  size = 'medium',
}: MachineStatusIndicatorProps): React.JSX.Element | null => {
  const { t } = useTranslation();

  const getStatusPillSize = (): StatusPillProps['size'] => {
    if (size === 'medium') {
      return 'large';
    }

    return 'small';
  };

  const statusItem = useMemo((): Optional<IMachineStatusItemData> => {
    let machineStatus = null;

    if (isString(status)) return MachineStatusItemData[status];

    if (!isString(lastActivityAt)) {
      return MachineStatusItemData[DataStatus.NoData];
    }

    machineStatus = getMachineStatus(lastActivityAt);

    if (!machineStatus) {
      return null;
    }

    return machineStatus;
  }, [lastActivityAt, status]);

  if (!statusItem) {
    return null;
  }

  return (
    <StyledSiteStatusIndicator className={classnames(className, 'machine-connection-status')}>
      <SiteStatusIndicatorGlobalStyles />
      <Tooltip
        title={<SiteStatusIndicatorTooltip statusItem={statusItem} />}
        overlayClassName="machine-connection-status-tooltip__overlay"
        arrow={{ pointAtCenter: true }}
      >
        <div className="machine-connection-status__tooltip-wrapper">
          <StatusPill color={statusItem.color} size={getStatusPillSize()}>
            <SvgIcon name={statusItem.icon} className="machine-connection-status__icon" />
          </StatusPill>
          {showText && <span className="machine-connection-status__text">&nbsp; {t(statusItem.title)}</span>}
        </div>
      </Tooltip>
    </StyledSiteStatusIndicator>
  );
};
